import { AxiosError } from "axios";
import { observer } from "mobx-react";
import { FC, useEffect } from "react";
import {
  Navigate,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { finalize, lastValueFrom } from "rxjs";
import { AccountModule } from "src/Modules";
import { ROUTE_URLS } from "src/Shared/Enums/RouteUrls.enum";
import { useStore } from "src/Shared/Hooks/UseStore.hook";
import Layout from "src/Shared/Layout/Layout";
import { PUBLIC_ROUTES } from "src/Shared/Tools/PUBLIC_ROUTES";
import { buildLoginRedirect } from "src/Shared/Tools/RedirectUrl";

const Authorization: FC = () => {
  const {
    rootStore: { authStore, uiStore },
  } = useStore();

  const location = useLocation();
  const navigate = useNavigate();

  /**
   * Where to send someone who is not signed in, carrying the whole URL they asked for.
   *
   * This used to append the current query string to the login route, which meant the path they
   * wanted was thrown away and only its query survived — so a shared or bookmarked link could never
   * come back to the right place (bug 5670). The intended URL is now passed whole in a named
   * parameter, encoded, so a query of its own cannot be mistaken for the destination.
   */
  const getRedirectURL = () => {
    if (PUBLIC_ROUTES.some((x) => x == location.pathname)) {
      return location.pathname;
    }
    return buildLoginRedirect(location.pathname, location.search);
  };

  useEffect(() => {
    if (!authStore.hasToken) return navigate(getRedirectURL());
    uiStore.setLoader(true);
    authStore
      .validateToken()
      .pipe(
        finalize(() => {
          uiStore.setLoader(false);
        })
      )
      .subscribe({
        next: (user) => {
          authStore.setUserDetail(user);
          if (location.pathname === ROUTE_URLS.LOGIN) navigate(ROUTE_URLS.HOME);
          // Otherwise stay exactly where the user asked to be, query string included.
          //
          // This branch used to read any query string as a redirect instruction — it navigated to
          // "/" plus whatever followed the first "=" and discarded the path. Every query-string URL
          // in the app therefore died on a full load: ?step=3 went to /3, ?section=contacts to
          // /contacts, ?focus=<id> to /<id>, none of which match a route, so all of them fell
          // through to the Home grid. Refreshing mid-wizard, bookmarking a step and opening a
          // search result in a new tab were all broken by it (bugs 5670, 5707).
          else navigate(`${location.pathname}${location.search}`);
          return;
        },
        error: (error: AxiosError) => {
          navigate(getRedirectURL());
        },
      });
  }, []);
  return (
    // <Layout>
      <Outlet />
    // </Layout>
  );
};

export default observer(Authorization);
